import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import * as R from "ramda";
import { useTranslation } from "react-i18next";
import { Add, CheckBox } from "@material-ui/icons";
import { useHistory, useLocation, useParams } from "react-router";
import { get, set } from "@totalsoft/react-state-lens";
import { useChangeTrackingLens } from "@totalsoft/change-tracking-react";
import {
  ACTIVATE_QUOTE_FROM_ESTIMATION,
  SAVE_PARTNER,
  SAVE_PARTNER_FOR_SOLE_TRADER_FEATURE,
  SAVE_QUOTE_PARTNERS,
  UPDATE_QUOTE,
} from "../mutations";
import { initialClientForCompanyFilter } from "../constants";
import { partnerForSoleTraderFeatureValidator, partnerValidator } from "../validators";
import { useValidation } from "@totalsoft/pure-validations-react";
import { over, sequence, useStateLens } from "@totalsoft/react-state-lens";
import { emptyArray, emptyObject } from "constants/defaultValues";
import { countryCodeConstants } from "features/common/constants";
import { quoteSteps } from "features/quote/constants";
import { useMutationWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { useContentFooter, useContentHeader } from "hooks/mainContent";
import { getTenant } from "providers/TenantProvider";
import QuoteFooter from "features/quote/components/QuoteFooter";
import QuoteStepper from "features/quote/components/QuoteStepper";
import ClientContainer from "./ClientContainer";
import ActionsDialog from "features/common/components/ActionsDialog";
import ColesseeContainerContent from "./ColesseeContainerContent";
import ColesseeCard from "./ColesseeCard";
import ColesseeContainer from "./ColesseeContainer";
import GdprConsent from "./GdprConsent";
import useUserData from "hooks/useUserData";
import { addToast } from "components/notifications";
import { GET_ESTIMATION_SELECTED_PARTNER, CHECK_CONSENT } from "../queries";
import { QUOTE_QUERY } from "features/client/queries";
import { partnerConsents } from "constants/partnerConsents";
import { invalidateApolloClientCache } from "utils/functions";
import { useApolloClient } from "@apollo/client";
import { LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";

function ClientStepContainer() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const client = useApolloClient();
  const userData = useUserData();
  const [, setFooter] = useContentFooter();
  const [, setHeader] = useContentHeader();
  const params = useParams();
  const quoteId = Number(params.quoteId);
  const quoteExists = Boolean(quoteId);
  const isIndividualLens = useStateLens(false);
  const isSoleTraderFeatureLens = useStateLens(false);

  const [gdprIsOpen, setGdprIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { countryCode } = getTenant();
  const colesseeFunctionality = countryCode === countryCodeConstants.china;

  const withColesseeLens = useStateLens(false);
  const withColessee = withColesseeLens |> get;

  const quotePartnersLens = useStateLens(emptyArray);
  const quotePartners = quotePartnersLens |> get;

  const [selectedClientLens, , resetSelectedClientLens] = useChangeTrackingLens(emptyObject);
  const selectedClient = selectedClientLens |> get;

  const { partnerId: clientId, name: clientName } =
    (withColessee ? quotePartners.find((p) => p.roleId === -1) : selectedClientLens |> get) || emptyObject;

  const [filterLens, , resetFiltersLens] = useChangeTrackingLens(initialClientForCompanyFilter);
  const [partnerListLens, , resetPartnerList] = useChangeTrackingLens(location?.state?.partnerList || emptyArray);

  const { data, loading: loadingQuote } = useQueryWithErrorHandling(QUOTE_QUERY, {
    skip: !quoteExists,
    variables: { quoteId },
  });
  const quote = data?.quote;
  const isReadOnly = (quote?.isEstimation && quote?.partnerId !== null) || false;

  const { loading: loadingSelectedPartner } = useQueryWithErrorHandling(GET_ESTIMATION_SELECTED_PARTNER, {
    skip: !quote?.isEstimation || !quote?.partnerId,
    variables: { quoteId: Number(quoteId) },
    onCompleted: (data) => {
      resetSelectedClientLens(data?.getEstimationSelectedPartner);
      resetPartnerList([data?.getEstimationSelectedPartner]);
    },
  });

  const partnerList = partnerListLens |> get;

  const [partnerSearchPopoverState, setPartnerSearchPopoverState] = useState({ open: false, index: null });

  const resetState = useCallback(() => {
    (partnerListLens |> set)(emptyArray);
    resetFiltersLens(initialClientForCompanyFilter);
    resetSelectedClientLens(emptyObject);
    (isIndividualLens |> set)(false);
  }, [partnerListLens, isIndividualLens, resetFiltersLens, resetSelectedClientLens]);

  const handleOpenPartnerSearchPopover = useCallback(
    (index) => {
      resetState();
      setPartnerSearchPopoverState((prev) => ({ ...prev, open: true, index: index }));
    },
    [resetState]
  );

  const handleClosePartnerSearchPopover = useCallback(() => {
    resetState();
    setPartnerSearchPopoverState((prev) => ({ ...prev, open: false }));
  }, [resetState]);

  const btnNextEnabled = useMemo(() => {
    if (isSaving) return false;

    return withColessee
      ? quotePartners.length > 0 &&
          !quotePartners.some((p) => !p?.partnerId) &&
          quotePartners.filter((p) => p.roleId === -1).length === 1
      : selectedClient?.partnerId ||
          (selectedClient?.origin && selectedClient?.bureauId) ||
          selectedClient?.createClient ||
          ((isIndividualLens |> get) && partnerList.length > 0) ||
          ((isSoleTraderFeatureLens |> get) && partnerList.length > 0);
  }, [isSaving, withColessee, quotePartners, selectedClient, isIndividualLens, partnerList, isSoleTraderFeatureLens]);

  const [validation, validate] = useValidation(
    userData?.allowSoleTraderSearch ? partnerForSoleTraderFeatureValidator : partnerValidator
  );
  const [savePartner] = useMutationWithErrorHandling(SAVE_PARTNER);
  const [savePartnerForSoleTraderFeature] = useMutationWithErrorHandling(SAVE_PARTNER_FOR_SOLE_TRADER_FEATURE);

  const handleSavePartner = useCallback(async () => {
    const selectedClient = selectedClientLens |> get;
    if (!selectedClient || selectedClient?.partnerId) {
      return selectedClient?.partnerId;
    }

    if (
      selectedClient.createClient &&
      !validate({
        ...selectedClient,
        isIndividual: isIndividualLens |> get,
        isSoleTraderFeature: isSoleTraderFeatureLens |> get,
        forUnitedKingdom: countryCode === countryCodeConstants.unitedKingdom,
        dealerDrivenConsent: userData?.dealerDrivenConsent,
      })
    ) {
      return;
    }

    const { data } =
      userData?.allowSoleTraderSearch && !selectedClient?.isSelectFromList
        ? await savePartnerForSoleTraderFeature({
            variables: {
              input: {
                isSoleTrader: isSoleTraderFeatureLens |> get,
                companyData:
                  isSoleTraderFeatureLens |> get
                    ? null
                    : {
                        companyName: selectedClient.companyName,
                        registrationNumber: selectedClient.registrationNumber,
                        registrationDate:
                          selectedClient?.registrationDate != null
                            ? moment(selectedClient?.registrationDate).format("YYYY-MM-DDT00:00:00")
                            : null,
                        legalFormId: selectedClient.legalFormId,
                        areaOfActivityId: selectedClient.areaOfActivityId,
                        countryId: selectedClient.countryId,
                        countyId: selectedClient.countyId,
                        cityId: selectedClient.cityId,
                        street: selectedClient.street,
                        streetNumber: selectedClient.streetNumber,
                        zip: selectedClient.zip,
                        neighborhood: selectedClient.neighborhood,
                      },
                persons: Object.keys(selectedClient).includes("persons")
                  ? selectedClient.persons.map((p) => ({
                      firstName: p.firstName,
                      lastName: p.lastName,
                      nationality: p.nationality,
                      genderId: p.genderId,
                      birthDate: moment(p.birthDate).format("YYYY-MM-DDT00:00:00"),
                      pin: p.pin,
                      email: p.email,
                      phone: p.phone,
                      areaOfActivityId: p.areaOfActivityId,
                      registrationDate:
                        p?.registrationDate != null ? moment(p?.registrationDate).format("YYYY-MM-DDT00:00:00") : null,
                      neighborhood: p.neighborhood,
                      countryId: selectedClient.countryId,
                      countyId: p.countyId,
                      cityId: p.cityId,
                      street: p.street,
                      streetNumber: p.streetNumber,
                      zip: p.zip,
                      existOfficialAddress: p.addOfficialAddress,
                      officialPartnerAddress: p.addOfficialAddress
                        ? selectedClient.persons[0].officialPartnerAddress
                        : null,
                    }))
                  : [],
              },
            },
          })
        : await savePartner({
            variables: {
              input: {
                firstName: selectedClient?.firstName,
                lastName: selectedClient?.lastName,
                email: selectedClient?.email,
                phone: selectedClient?.phone,
                genderId: selectedClient?.genderId,
                birthDate:
                  selectedClient?.birthDate != null
                    ? moment(selectedClient?.birthDate).format("YYYY-MM-DDT00:00:00")
                    : null,
                pin: selectedClient?.pin,
                identityCardNo: selectedClient?.identityCardNo,
                companyName: selectedClient?.name || selectedClient?.companyName,
                legalFormId: selectedClient?.legalFormId,
                taxId: selectedClient?.taxId,
                street1: selectedClient?.street1,
                street2: selectedClient?.street2,
                number: selectedClient?.number,
                city: selectedClient?.city,
                countryId: selectedClient?.countryId,
                zip: selectedClient?.zip,
                state: selectedClient?.state,
                origin: selectedClient?.origin,
                bureauId: selectedClient?.bureauId,
                isIndividual: isIndividualLens |> get,
              },
            },
          });

    const newPartnerId =
      userData?.allowSoleTraderSearch && !selectedClient?.isSelectFromList
        ? data?.savePartnerForSoleTraderFeature
        : data?.savePartner;
    const newPartnerName = selectedClient?.companyName || selectedClient?.name;

    (selectedClientLens |> set)({ ...selectedClient, partnerId: newPartnerId, name: newPartnerName });

    return newPartnerId;
  }, [
    countryCode,
    userData?.allowSoleTraderSearch,
    userData?.dealerDrivenConsent,
    selectedClientLens,
    isIndividualLens,
    isSoleTraderFeatureLens,
    savePartnerForSoleTraderFeature,
    savePartner,
    validate,
  ]);

  const { data: checkConsentData } = useQueryWithErrorHandling(CHECK_CONSENT, {
    skip:
      !selectedClient?.partnerId ||
      selectedClient?.createClient ||
      userData?.requestAlwaysConsent ||
      (!userData?.dealerDrivenConsent && !userData?.customerDrivenConsent),
    variables: { partnerId: selectedClient?.partnerId },
  });

  const [updateQuote] = useMutationWithErrorHandling(UPDATE_QUOTE);
  const [saveQuotePartners] = useMutationWithErrorHandling(SAVE_QUOTE_PARTNERS);
  const [activateQuoteFromEstimation] = useMutationWithErrorHandling(ACTIVATE_QUOTE_FROM_ESTIMATION);

  const handleComplete = useCallback(async () => {
    setIsSaving(true);
    try {
      const { partnerId } = (selectedClientLens |> get) || partnerList[0];

      const clientId = withColessee
        ? quotePartners.find((p) => p.roleId === -1).partnerId
        : partnerId || (await handleSavePartner());

      if (!clientId) {
        setIsSaving(false);
        return;
      }

      if (quoteExists) {
        if (quote.partnerId !== clientId) {
          await updateQuote({ variables: { input: { quoteId, partnerId: clientId } } });
        }

        const quotePartnersForSave = quotePartners
          ?.filter((p) => p.roleId !== -1)
          ?.map((p) => ({ quoteId, partnerId: p.partnerId, roleId: p.roleId }));

        if (quotePartnersForSave?.length) {
          await saveQuotePartners({ variables: { input: { quoteId, partners: quotePartnersForSave } } });
        }
      }

      if (
        userData?.requestAlwaysConsent ||
        (checkConsentData !== undefined && !checkConsentData?.checkConsent) ||
        ((selectedClient?.createClient || !selectedClient?.partnerId) &&
          (userData?.dealerDrivenConsent || userData?.customerDrivenConsent))
      ) {
        setGdprIsOpen(true);
        return;
      }

      if (quote?.isEstimation) {
        await activateQuoteFromEstimation({ variables: { quoteId: Number(quoteId) } });
      }

      history.push({
        pathname: `/quotes/${quoteId || "new"}/assets`,
        state: { clientId, quotePartners: quotePartners.filter((p) => p.roleId !== -1), partnerList },
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    quoteExists,
    history,
    quoteId,
    selectedClientLens,
    partnerList,
    withColessee,
    quotePartners,
    handleSavePartner,
    updateQuote,
    saveQuotePartners,
    activateQuoteFromEstimation,
    quote?.isEstimation,
    checkConsentData,
    userData?.requestAlwaysConsent,
    quote?.partnerId,
    userData?.customerDrivenConsent,
    userData?.dealerDrivenConsent,
    selectedClient,
  ]);

  const handleAddQuotePartner = useCallback(() => {
    (quotePartnersLens |> set)([...(quotePartnersLens |> get), {}]);
  }, [quotePartnersLens]);

  const handleRemoveQuotePartner = useCallback((index) => over(quotePartnersLens, R.remove(index, 1)), [
    quotePartnersLens,
  ]);

  const handleCloseGdprConsent = useCallback(
    (quoteId, existingQuote, consentCode) => {
      if (quoteId) {
        invalidateApolloClientCache(client, { __typename: "Quote", quoteId: quoteId });
        invalidateApolloClientCache(client, { __typename: "Query", quoteId: quoteId }, [
          "getSelectedPartnerContactPersons",
        ]);
      }

      const clientId = withColessee
        ? quotePartners.find((p) => p.roleId === -1)?.partnerId
        : (selectedClientLens |> get)?.partnerId;

      if (!consentCode) {
        const pathname = quoteId ? `/quotes/${quoteId}/assets` : `/estimations/new/assets`;
        history.push({ pathname, state: { clientId } });
        !existingQuote && addToast(t("ToastNotifications.GenerateEstimationMessageWithoutTimer"), "warning", false);
        return;
      }

      if (consentCode === partnerConsents.dealerConsent) {
        history.push({
          pathname: `/quotes/${quoteId}/assets`,
          state: { clientId, quotePartners: quotePartners.filter((p) => p.roleId !== -1) },
        });
      }

      if (consentCode === partnerConsents.customerConsent) {
        history.push({ pathname: `/quotes/${quoteId}/assets` });
        !existingQuote && addToast(t("ToastNotifications.GenerateEstimationMessageWithTimer"), "warning", true);
      }
    },
    [history, t, selectedClientLens, quotePartners, client, withColessee]
  );

  useEffect(() => {
    setHeader(
      <QuoteStepper
        quoteId={quoteId}
        activeStepId={quoteSteps.client}
        nextStepId={quoteSteps.assets}
        disabledNextStep={!btnNextEnabled}
        onHandleNextStep={handleComplete}
      />
    );
  }, [t, setHeader, quoteId, btnNextEnabled, handleComplete]);

  useEffect(() => {
    setFooter(
      <QuoteFooter
        btnBackLabel={t("Client.NewPartner")}
        btnNextLabel={t("General.SelectAsset")}
        btnBackIcon={<Add />}
        btnNextIcon={<CheckBox />}
        handleBack={handleAddQuotePartner}
        handleComplete={handleComplete}
        btnBackExists={withColessee}
        btnBackDisabled={false}
        btnNextDisabled={!btnNextEnabled}
      />
    );
  }, [t, setFooter, withColessee, btnNextEnabled, selectedClient, handleAddQuotePartner, handleComplete]);

  if (loadingQuote || loadingSelectedPartner) {
    return <LoadingFakeText lines={10} />;
  }

  return (
    <>
      {colesseeFunctionality && (
        <ColesseeCard withColesseeLens={withColesseeLens} quotePartnersLens={quotePartnersLens} />
      )}
      {withColessee && (
        <>
          {(quotePartnersLens |> sequence).map((quotePartnerLens, index) => (
            <ColesseeContainer
              key={index}
              index={index}
              quotePartnerLens={quotePartnerLens}
              onAdd={handleOpenPartnerSearchPopover}
              onRemove={handleRemoveQuotePartner}
            />
          ))}
          {partnerSearchPopoverState.open && (
            <ActionsDialog
              fullWidth={true}
              maxWidth={true}
              open={partnerSearchPopoverState.open}
              handleClose={handleClosePartnerSearchPopover}
              title={t("Client.SelectPartner")}
              initialState={{
                filterLens,
                isIndividualLens,
                partnerListLens,
                selectedClientLens,
                validation,
                resetFiltersLens,
                resetSelectedClientLens,
                resetPartnerList,
              }}
              Content={ColesseeContainerContent}
              actions={[
                {
                  caption: t("General.Confirm"),
                  disabled: R.isEmpty(selectedClient),
                  handle: async (state) => {
                    const partnerId = await handleSavePartner();

                    if (!partnerId) return false;

                    const quotePartnerLens = (quotePartnersLens |> sequence)[partnerSearchPopoverState.index];
                    const quotePartner = quotePartnerLens |> get;

                    const newQuotePartner = {
                      ...(state.selectedClientLens |> get),
                      partnerId,
                      roleId: quotePartner.roleId,
                      name:
                        state.selectedClientLens?.createClient |> get
                          ? isIndividualLens |> get
                            ? t("Client.Individual")
                            : state.selectedClientLens.companyName |> get
                          : state.selectedClientLens?.name |> get,
                    };

                    (quotePartnerLens |> set)(newQuotePartner);

                    resetState();
                    return true;
                  },
                },
              ]}
            />
          )}
        </>
      )}
      {!withColessee && (
        <ClientContainer
          filterLens={filterLens}
          isIndividualLens={isIndividualLens}
          partnerListLens={partnerListLens}
          selectedClientLens={selectedClientLens}
          resetSelectedClientLens={resetSelectedClientLens}
          resetFiltersLens={resetFiltersLens}
          validation={validation}
          isReadOnly={isReadOnly}
          isSoleTraderFeatureLens={isSoleTraderFeatureLens}
        />
      )}
      {gdprIsOpen && (
        <GdprConsent
          quoteId={quoteId}
          partnerId={clientId}
          partnerName={clientName}
          onCloseGdprConsent={handleCloseGdprConsent}
        />
      )}
    </>
  );
}

export default ClientStepContainer;
